import { QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { AppLayout } from '@/components/layout/AppLayout'
import { ProtectedRoute } from '@/components/routes/ProtectedRoute'
import { PublicRoute } from '@/components/routes/PublicRoute'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { FullPageLoader } from '@/components/shared/LoadingSpinner'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AbilityProvider } from '@/contexts/AbilityContext'
import { queryClient } from '@/hooks/useAuth'
import { store } from '@/store'

import { APP_ROUTES, ROUTE_PERMISSIONS } from './libs/constants'

// Auth pages
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const Login = lazy(() => import('./pages/Login'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))

// Standalone pages (no layout)
const AccessDenied = lazy(() => import('./pages/AccessDenied'))
const NotFound = lazy(() => import('./pages/NotFound'))
const SelectOrganization = lazy(() => import('./pages/SelectOrganization'))

// Main pages
const Account = lazy(() => import('./pages/Account'))
const Billing = lazy(() => import('./pages/invoicing'))
const InvoiceDetails = lazy(() => import('./pages/invoicing/InvoiceDetails'))
const NewInvoice = lazy(() => import('./pages/invoicing/NewInvoice'))
const Booking = lazy(() => import('./pages/booking'))
const NewBooking = lazy(() => import('./pages/booking/NewBooking'))
const Campaigns = lazy(() => import('./pages/Campaigns'))
const CategoriesPage = lazy(() => import('./pages/categories'))
const CategoryDetails = lazy(() => import('./pages/categories/CategoryDetails'))
const NewCategory = lazy(() => import('./pages/categories/NewCategory'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))
const CommunicationPage = lazy(() => import('./pages/communication'))
const TemplateDetails = lazy(() => import('./pages/communication/TemplateDetails'))
const TemplateEditor = lazy(() => import('./pages/communication/TemplateEditor'))
const CustomersPage = lazy(() => import('./pages/customers'))
const CustomerDetails = lazy(() => import('./pages/customers/CustomerDetails'))
const NewCompany = lazy(() => import('./pages/customers/NewCompany'))
const NewCustomer = lazy(() => import('./pages/customers/NewCustomer'))
const CompanyDetails = lazy(() => import('./pages/customers/CompanyDetails'))
const Economy = lazy(() => import('./pages/Economy'))
const Index = lazy(() => import('./pages/Index'))
const Jobs = lazy(() => import('./pages/jobs'))
const JobDetails = lazy(() => import('./pages/jobs/JobDetails'))
const MyWorkspace = lazy(() => import('./pages/my-workspace'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Products = lazy(() => import('./pages/products'))
const NewProduct = lazy(() => import('./pages/products/NewProduct'))
const ProductDetails = lazy(() => import('./pages/products/ProductDetails'))
const RentalFleet = lazy(() => import('./pages/rental-fleet'))
const NewRentalVehicle = lazy(() => import('./pages/rental-fleet/NewRentalVehicle'))
const RentalVehicleDetails = lazy(() => import('./pages/rental-fleet/RentalVehicleDetails'))
const Services = lazy(() => import('./pages/settings/services'))
const NewService = lazy(() => import('./pages/settings/services/NewService'))
const ServiceDetails = lazy(() => import('./pages/settings/services/ServiceDetails'))
const BranchInfo = lazy(() => import('./pages/settings/BranchInfo'))
const Integrations = lazy(() => import('./pages/settings/Integrations'))
const NewStaff = lazy(() => import('./pages/settings/staff/NewStaff'))
const AbsenceTypes = lazy(() => import('./pages/settings/absenceTypes'))
const NewAbsenceType = lazy(() => import('./pages/settings/absenceTypes/NewAbsenceType'))
const AbsenceTypeDetails = lazy(() => import('./pages/settings/absenceTypes/AbsenceTypeDetails'))
const Packages = lazy(() => import('./pages/settings/packages'))
const NewPackage = lazy(() => import('./pages/settings/packages/NewPackage'))
const PackageDetails = lazy(() => import('./pages/settings/packages/PackageDetails'))
const Permissions = lazy(() => import('./pages/settings/Permissions'))
const Qualifications = lazy(() => import('./pages/settings/qualifications'))
const NewQualification = lazy(() => import('./pages/settings/qualifications/NewQualification'))
const QualificationDetails = lazy(
  () => import('./pages/settings/qualifications/QualificationDetails'),
)
const SettingsLayout = lazy(() => import('./pages/settings/SettingsLayout'))
const Staff = lazy(() => import('./pages/settings/staff'))
const StaffDetails = lazy(() => import('./pages/settings/staff/StaffDetails'))
const NewStore = lazy(() => import('./pages/settings/stores/NewStore'))
const StoreDetails = lazy(() => import('./pages/settings/stores/StoreDetails'))
const Stores = lazy(() => import('./pages/settings/stores'))
const UnionAgreements = lazy(() => import('./pages/settings/union-agreements'))
const NewUnionAgreement = lazy(() => import('./pages/settings/union-agreements/NewUnionAgreement'))
const UnionAgreementDetails = lazy(
  () => import('./pages/settings/union-agreements/UnionAgreementDetails'),
)
const WeeklyNotesSettings = lazy(() => import('./pages/settings/WeeklyNotes'))
const WorkingHours = lazy(() => import('./pages/settings/WorkingHours'))
const TeamOverviewHub = lazy(() => import('./pages/employee-approvals/TeamOverviewHub'))
const Vehicles = lazy(() => import('./pages/vehicles'))
const NewVehicle = lazy(() => import('./pages/vehicles/NewVehicle'))
const VehicleDetails = lazy(() => import('./pages/vehicles/VehicleDetails'))

queryClient.setDefaultOptions({
  queries: {
    retry: 1,
    refetchOnWindowFocus: false,
  },
})

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <AbilityProvider>
        <TooltipProvider>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Suspense fallback={<FullPageLoader />}>
                <Routes>
                  {/* Auth pages - public only */}
                  <Route
                    path={APP_ROUTES.auth.login}
                    element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path={APP_ROUTES.auth.forgotPassword}
                    element={
                      <PublicRoute>
                        <ForgotPassword />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path={APP_ROUTES.auth.resetPassword}
                    element={
                      <PublicRoute>
                        <ResetPassword />
                      </PublicRoute>
                    }
                  />

                  {/* Auth-required, no layout */}
                  <Route
                    path={APP_ROUTES.selectOrganization}
                    element={
                      <ProtectedRoute>
                        <SelectOrganization />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected app pages - single auth check + layout wrapper */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<AppLayout />}>
                      <Route index element={<Index />} />

                      {/* Customers */}
                      <Route
                        path={APP_ROUTES.customer.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
                            <CustomersPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.customer.new}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.customers.create}>
                            <NewCustomer />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.customer.company.create}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.company.create}>
                            <NewCompany />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={`${APP_ROUTES.customer.company.base}/:id`}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.company.read}>
                            <CompanyDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={`${APP_ROUTES.customer.base}/:id`}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
                            <CustomerDetails />
                          </ProtectedRoute>
                        }
                      />

                      {/* Vehicles */}
                      <Route
                        path={APP_ROUTES.vehicle.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.read}>
                            <Vehicles />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.vehicle.new}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.create}>
                            <NewVehicle />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={`${APP_ROUTES.vehicle.base}/:id`}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.read}>
                            <VehicleDetails />
                          </ProtectedRoute>
                        }
                      />

                      {/* Jobs */}
                      <Route
                        path={APP_ROUTES.job.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.jobs.read}>
                            <Jobs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={`${APP_ROUTES.job.base}/:id`}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.jobs.read}>
                            <JobDetails />
                          </ProtectedRoute>
                        }
                      />

                      {/* Booking */}
                      <Route
                        path={APP_ROUTES.booking.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.booking.create}>
                            <Booking />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.booking.new()}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.booking.create}>
                            <NewBooking />
                          </ProtectedRoute>
                        }
                      />

                      {/* Pricing */}
                      <Route
                        path={APP_ROUTES.pricing}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.pricing.read}>
                            <Pricing />
                          </ProtectedRoute>
                        }
                      />

                      {/* Workspace */}
                      <Route
                        path={APP_ROUTES.myWorkspace}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.myWorkspace.read}>
                            <MyWorkspace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.teamOverview}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.teamOverview.read}>
                            <TeamOverviewHub />
                          </ProtectedRoute>
                        }
                      />
                      {/* Legacy work redirects */}
                      <Route
                        path={APP_ROUTES.leaves}
                        element={<Navigate to={`${APP_ROUTES.myWorkspace}?tab=leaves`} replace />}
                      />
                      <Route
                        path={APP_ROUTES.workLogs}
                        element={<Navigate to={APP_ROUTES.myWorkspace} replace />}
                      />
                      <Route
                        path={`${APP_ROUTES.workLogs}/my`}
                        element={<Navigate to={APP_ROUTES.myWorkspace} replace />}
                      />
                      <Route
                        path={`${APP_ROUTES.workLogs}/team`}
                        element={<Navigate to={APP_ROUTES.teamOverview} replace />}
                      />

                      {/* Invoicing */}
                      <Route
                        path={APP_ROUTES.invoicing.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
                            <Billing />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.invoicing.new}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.billing.create}>
                            <NewInvoice />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={`${APP_ROUTES.invoicing.base}/:id`}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
                            <InvoiceDetails />
                          </ProtectedRoute>
                        }
                      />
                      {/* Legacy billing redirects */}
                      <Route
                        path={APP_ROUTES.billing}
                        element={<Navigate to={APP_ROUTES.invoicing.base} replace />}
                      />
                      <Route
                        path={`${APP_ROUTES.billing}/new`}
                        element={<Navigate to={APP_ROUTES.invoicing.new} replace />}
                      />
                      <Route
                        path={`${APP_ROUTES.billing}/:id`}
                        element={<Navigate to={APP_ROUTES.invoicing.base} replace />}
                      />

                      {/* Economy */}
                      <Route
                        path={APP_ROUTES.economy}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
                            <Economy />
                          </ProtectedRoute>
                        }
                      />

                      {/* Products */}
                      <Route
                        path={APP_ROUTES.product.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.products.read}>
                            <Products />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.product.new}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.products.create}>
                            <NewProduct />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={`${APP_ROUTES.product.base}/:id`}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.products.read}>
                            <ProductDetails />
                          </ProtectedRoute>
                        }
                      />

                      {/* Rental Fleet */}
                      <Route
                        path={APP_ROUTES.rentCar.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.read}>
                            <RentalFleet />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.rentCar.new}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.create}>
                            <NewRentalVehicle />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={`${APP_ROUTES.rentCar.base}/:id`}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.read}>
                            <RentalVehicleDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={APP_ROUTES.loanCars}
                        element={<Navigate to={APP_ROUTES.rentCar.base} replace />}
                      />

                      {/* Campaigns */}
                      <Route
                        path={APP_ROUTES.campaigns}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.campaigns.read}>
                            <Campaigns />
                          </ProtectedRoute>
                        }
                      />

                      {/* Legacy redirects for sections moved to settings */}
                      <Route
                        path={APP_ROUTES.category.base}
                        element={<Navigate to={APP_ROUTES.settings.category.base} replace />}
                      />
                      <Route
                        path={APP_ROUTES.communication.base}
                        element={<Navigate to={APP_ROUTES.settings.communication.base} replace />}
                      />

                      {/* Settings */}
                      <Route
                        path={APP_ROUTES.settings.base}
                        element={
                          <ProtectedRoute {...ROUTE_PERMISSIONS.settings.read}>
                            <SettingsLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route
                          index
                          element={<Navigate to={APP_ROUTES.settings.branchInfo} replace />}
                        />
                        <Route path="branch-info" element={<BranchInfo />} />
                        <Route path="working-hours" element={<WorkingHours />} />
                        <Route path="staff" element={<Staff />} />
                        <Route path="staff/new" element={<NewStaff />} />
                        <Route path="staff/:id" element={<StaffDetails />} />
                        <Route path="permissions" element={<Permissions />} />
                        <Route path="stores" element={<Stores />} />
                        <Route path="stores/new" element={<NewStore />} />
                        <Route path="stores/:id" element={<StoreDetails />} />
                        <Route path="qualifications" element={<Qualifications />} />
                        <Route path="qualifications/new" element={<NewQualification />} />
                        <Route path="qualifications/:id" element={<QualificationDetails />} />
                        <Route path="union-agreements" element={<UnionAgreements />} />
                        <Route path="union-agreements/new" element={<NewUnionAgreement />} />
                        <Route path="union-agreements/:id" element={<UnionAgreementDetails />} />
                        <Route path="packages" element={<Packages />} />
                        <Route path="packages/new" element={<NewPackage />} />
                        <Route path="packages/:id" element={<PackageDetails />} />
                        <Route path="absence-types" element={<AbsenceTypes />} />
                        <Route path="absence-types/new" element={<NewAbsenceType />} />
                        <Route path="absence-types/:id" element={<AbsenceTypeDetails />} />
                        <Route path="weekly-notes" element={<WeeklyNotesSettings />} />
                        {/* <Route path="integrations" element={<Integrations />} /> */}
                        <Route path="categories" element={<CategoriesPage />} />
                        <Route
                          path="categories/service/new"
                          element={<NewCategory type="service" />}
                        />
                        <Route
                          path="categories/product/new"
                          element={<NewCategory type="product" />}
                        />
                        <Route
                          path="categories/service/:id"
                          element={<CategoryDetails type="service" />}
                        />
                        <Route
                          path="categories/product/:id"
                          element={<CategoryDetails type="product" />}
                        />
                        <Route path="services" element={<Services />} />
                        <Route path="services/new" element={<NewService />} />
                        <Route path="services/:id" element={<ServiceDetails />} />
                        <Route path="communication" element={<CommunicationPage />} />
                        <Route
                          path="communication/email/new"
                          element={<TemplateEditor type="email" mode="create" />}
                        />
                        <Route
                          path="communication/sms/new"
                          element={<TemplateEditor type="sms" mode="create" />}
                        />
                        <Route
                          path="communication/email/:id"
                          element={<TemplateDetails type="email" />}
                        />
                        <Route
                          path="communication/sms/:id"
                          element={<TemplateDetails type="sms" />}
                        />
                      </Route>

                      {/* Account */}
                      <Route path={APP_ROUTES.account} element={<Account />} />
                      <Route path={APP_ROUTES.changePassword} element={<ChangePassword />} />
                    </Route>
                  </Route>

                  {/* Fallback */}
                  <Route path={APP_ROUTES.accessDenied} element={<AccessDenied />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </AbilityProvider>
    </QueryClientProvider>
  </Provider>
)

export default App
